'use client'

import { motion } from 'framer-motion'
import { Calendar, Clock, MapPin, ArrowRight, Video, Users } from 'lucide-react'
import { Navigation } from '../../components/organisms/Navigation'
import { Footer } from '../../components/organisms/Footer'
import { cn } from '../../utils/cn'
import { trackCalendlyClick } from '../../lib/tracking'

interface Event {
  id: string
  title: string
  description: string
  date: string
  time: string
  type: 'webinar' | 'workshop' | 'masterclass'
  location: string
  speakers: string[]
  registrationUrl: string
  isPast: boolean
}

const events: Event[] = [
  {
    id: '1',
    title: 'AI Readiness Masterclass: From Assessment to Action',
    description: 'Learn how to evaluate your organization\'s AI maturity across 23 dimensions and build a transformation roadmap that actually works.',
    date: '2026-04-10',
    time: '2:00 PM EST',
    type: 'masterclass',
    location: 'Virtual',
    speakers: ['Alex Behm', 'Matty Squarzoni'],
    registrationUrl: 'https://calendly.com/alex-behmn/discovery-call',
    isPast: false,
  },
  {
    id: '2',
    title: 'Process Capture Demo: Video to SOP in Minutes',
    description: 'Live demonstration of the Adapt Engine\'s video process capture. Watch us turn a screen recording into a complete Standard Operating Procedure.',
    date: '2026-04-24',
    time: '1:00 PM EST',
    type: 'webinar',
    location: 'Virtual',
    speakers: ['Matty Squarzoni'],
    registrationUrl: 'https://calendly.com/alex-behmn/discovery-call',
    isPast: false,
  },
]

const typeIcons = {
  webinar: Video,
  workshop: Users,
  masterclass: Calendar,
}

const typeColors = {
  webinar: 'bg-blue-400/10 text-blue-400 border-blue-400/20',
  workshop: 'bg-green-400/10 text-green-400 border-green-400/20',
  masterclass: 'bg-blue-400/10 text-blue-400 border-blue-400/20',
}

export default function EventsPage() {
  const upcoming = events.filter(e => !e.isPast)
  const past = events.filter(e => e.isPast)

  return (
    <main className="min-h-screen hg-bg-primary hg-text-primary">
      <Navigation />

      {/* Hero */}
      <section className="pt-32 md:pt-40 pb-12 px-6">
        <div className="container max-w-4xl mx-auto text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-gendy text-4xl md:text-6xl bg-gradient-to-r from-white via-cyan-200 to-white bg-clip-text text-transparent mb-4"
          >
            Events & Webinars
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-diatype text-lg hg-text-secondary max-w-2xl mx-auto"
          >
            Join live sessions to learn AI transformation strategies, see product demos, and connect with practitioners.
          </motion.p>
        </div>
      </section>

      {/* Upcoming Events */}
      <section className="py-12 px-6">
        <div className="container max-w-5xl mx-auto">
          <h2 className="font-gendy text-2xl hg-text-primary mb-8">Upcoming Events</h2>

          {upcoming.length === 0 ? (
            <div className="text-center py-12 hg-text-muted font-diatype">
              No upcoming events. Check back soon or subscribe to be notified.
            </div>
          ) : (
            <div className="space-y-6">
              {upcoming.map((event, i) => {
                const TypeIcon = typeIcons[event.type]
                return (
                  <motion.div
                    key={event.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className={cn(
                      'p-6 md:p-8 rounded-2xl',
                      'bg-[var(--hg-bg-secondary)] border border-[var(--hg-border-color)]',
                      'hover:border-[var(--hg-cyan-text)]/20 transition-all duration-300'
                    )}
                  >
                    <div className="flex flex-col md:flex-row md:items-start gap-6">
                      {/* Date badge */}
                      <div className="flex-shrink-0 w-20 h-20 rounded-xl bg-[var(--hg-bg-tertiary)] flex flex-col items-center justify-center">
                        <span className="font-gendy text-2xl text-[var(--hg-cyan-text)]">
                          {new Date(event.date).getDate()}
                        </span>
                        <span className="font-diatype text-xs hg-text-muted uppercase">
                          {new Date(event.date).toLocaleString('en', { month: 'short' })}
                        </span>
                      </div>

                      {/* Content */}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={cn(
                            'text-[10px] px-2 py-0.5 rounded-full font-diatype border capitalize',
                            typeColors[event.type]
                          )}>
                            <TypeIcon className="w-3 h-3 inline mr-1" />
                            {event.type}
                          </span>
                        </div>

                        <h3 className="font-gendy text-xl hg-text-primary mb-2">
                          {event.title}
                        </h3>
                        <p className="font-diatype text-sm hg-text-secondary mb-4">
                          {event.description}
                        </p>

                        <div className="flex items-center gap-4 text-xs font-diatype hg-text-muted mb-4">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" /> {event.time}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> {event.location}
                          </span>
                          <span className="flex items-center gap-1">
                            <Users className="w-3 h-3" /> {event.speakers.join(', ')}
                          </span>
                        </div>

                        <a
                          href={event.registrationUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => trackCalendlyClick('event_registration')}
                          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-diatype text-sm font-medium text-black hover:shadow-[0_0_20px_rgba(97,216,254,0.3)] transition-all"
                          style={{ background: 'var(--hg-gradient-brand)' }}
                        >
                          Register Now
                          <ArrowRight className="w-4 h-4" />
                        </a>
                      </div>
                    </div>
                  </motion.div>
                )
              })}
            </div>
          )}
        </div>
      </section>

      {/* Past Events */}
      {past.length > 0 && (
        <section className="py-12 px-6">
          <div className="container max-w-5xl mx-auto">
            <h2 className="font-gendy text-2xl hg-text-primary mb-8">Past Events</h2>
            <div className="space-y-4">
              {past.map((event) => (
                <div
                  key={event.id}
                  className="p-4 rounded-xl bg-[var(--hg-bg-secondary)] border border-[var(--hg-border-color)] flex items-center gap-4"
                >
                  <div className="font-diatype text-sm hg-text-muted">
                    {new Date(event.date).toLocaleDateString('en', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </div>
                  <div className="font-diatype text-sm hg-text-secondary">{event.title}</div>
                  <span className={cn(
                    'text-[10px] px-2 py-0.5 rounded-full font-diatype border capitalize ml-auto',
                    typeColors[event.type]
                  )}>
                    {event.type}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Newsletter CTA */}
      <section className="py-16 px-6">
        <div className="container max-w-4xl mx-auto text-center">
          <h2 className="font-gendy text-2xl hg-text-primary mb-3">
            Host a Private Workshop
          </h2>
          <p className="font-diatype hg-text-secondary mb-6">
            Bring AI transformation training directly to your team with a customized masterclass.
          </p>
          <a
            href="https://calendly.com/alex-behmn"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-diatype font-medium text-black hover:shadow-[0_0_30px_rgba(97,216,254,0.4)] transition-all"
            style={{ background: 'var(--hg-gradient-brand)' }}
          >
            Discuss Your Workshop
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </section>

      <Footer />
    </main>
  )
}
